import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload } from "lucide-react";
import { SAPCandidate } from "@/data/sapStore";
import { regionsMapData } from "@/data/mapData";
import { uploadFile } from "@/utils/uploadHelpers";

interface AdminSapFormDialogProps {
  children?: React.ReactNode;
  candidate?: SAPCandidate | null;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSave: (candidate: SAPCandidate) => void;
}

export default function AdminSapFormDialog({ children, candidate, open, onOpenChange, onSave }: AdminSapFormDialogProps) {
  const [formData, setFormData] = useState<Partial<SAPCandidate>>({
    status: "valide",
    genre: "H"
  });
  const [isUploading, setIsUploading] = useState(false);

  // Reset form when opening/changing candidate
  useEffect(() => {
    if (candidate) {
      setFormData(candidate);
    } else {
      setFormData({
        id: `sap-${Date.now()}`,
        status: "valide",
        genre: "H",
        firstName: "",
        lastName: "",
        firstNameAr: "",
        lastNameAr: "",
        region: regionsMapData[0]?.name || "",
        province: "",
        symbol: "👤",
        photoUrl: "",
        focusFr: "",
        focusAr: "",
        sociopro: "",
        age: 30
      });
    }
  }, [candidate, open]);

  const handleChange = (field: keyof SAPCandidate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const url = await uploadFile(file);
      setFormData(prev => ({ ...prev, photoUrl: url }));
    } catch (error) {
      console.error("Erreur d'upload:", error);
      alert("Erreur lors de l'upload du fichier.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.firstName && formData.lastName && formData.region) {
      onSave(formData as SAPCandidate);
      if (onOpenChange) onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{candidate ? "Modifier le candidat SAP" : "Ajouter un candidat SAP"}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Noms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl">
            <div className="space-y-2">
              <Label>Prénom (FR)</Label>
              <Input required value={formData.firstName || ""} onChange={e => handleChange("firstName", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Nom (FR)</Label>
              <Input required value={formData.lastName || ""} onChange={e => handleChange("lastName", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">Prénom (AR)</Label>
              <Input required dir="rtl" value={formData.firstNameAr || ""} onChange={e => handleChange("firstNameAr", e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">Nom (AR)</Label>
              <Input required dir="rtl" value={formData.lastNameAr || ""} onChange={e => handleChange("lastNameAr", e.target.value)} />
            </div>
          </div>

          {/* Démographie & Visuels */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Genre</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.genre} onChange={e => handleChange("genre", e.target.value)}>
                <option value="H">Homme</option>
                <option value="F">Femme</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Âge</Label>
              <Input type="number" required value={formData.age || ""} onChange={e => handleChange("age", parseInt(e.target.value))} />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Catégorie Socioprofessionnelle</Label>
              <Input required value={formData.sociopro || ""} onChange={e => handleChange("sociopro", e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label>Symbole (Emoji)</Label>
              <Input required value={formData.symbol || ""} onChange={e => handleChange("symbol", e.target.value)} className="text-xl" />
            </div>
            <div className="space-y-2 md:col-span-3">
              <Label>Photo / Symbole du candidat</Label>
              <div className="flex items-center gap-4">
                {formData.photoUrl && (
                  <div className="w-10 h-10 shrink-0 rounded-full bg-muted border border-border overflow-hidden">
                    <img src={formData.photoUrl} alt="Aperçu" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="flex-1 relative">
                  <Input 
                    type="file" 
                    accept="image/*" 
                    onChange={handleFileUpload} 
                    className="cursor-pointer file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" 
                  />
                </div>
              </div>
              {isUploading && <p className="text-xs text-primary font-bold mt-2">Upload en cours...</p>}
            </div>
          </div>

          {/* Localisation */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-muted/30 p-4 rounded-xl">
            <div className="space-y-2">
              <Label>Région</Label>
              <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.region} onChange={e => handleChange("region", e.target.value)}>
                {regionsMapData.map(r => <option key={r.id} value={r.name}>{r.name}</option>)}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Province / Préfecture</Label>
              <Input required value={formData.province || ""} onChange={e => handleChange("province", e.target.value)} />
            </div>
          </div>

          {/* Axe Majeur */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Axe Majeur / Priorité (FR)</Label>
              <Textarea required value={formData.focusFr || ""} onChange={e => handleChange("focusFr", e.target.value)} rows={3} />
            </div>
            <div className="space-y-2">
              <Label className="text-right block">Axe Majeur / Priorité (AR)</Label>
              <Textarea required dir="rtl" value={formData.focusAr || ""} onChange={e => handleChange("focusAr", e.target.value)} rows={3} />
            </div>
          </div>

          {/* Statut */}
          <div className="space-y-2 bg-primary/5 p-4 rounded-xl border border-primary/20">
            <Label>Statut d'Accréditation</Label>
            <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-semibold" value={formData.status} onChange={e => handleChange("status", e.target.value)}>
              <option value="valide">Validé (Publié)</option>
              <option value="en_attente">En attente (Masqué)</option>
              <option value="rejete">Rejeté</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange && onOpenChange(false)} disabled={isUploading}>Annuler</Button>
            <Button type="submit" disabled={isUploading}>Sauvegarder</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
